import React, { Suspense, useEffect, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import PageLoader from '../common/PageLoader';
import {
  Avatar, Button, Drawer, Dropdown, Grid, Layout, Menu, Space, Typography,
} from 'antd';
import type { MenuProps } from 'antd';
import {
  LogoutOutlined, MenuOutlined, UserOutlined,
} from '@ant-design/icons';
import WorkWavLogo from '../common/WorkWavLogo';
import CountrySelector from '../common/CountrySelector';
import CookieBanner from '../common/CookieBanner';
import PWAInstallButton from '../common/PWAInstallButton';
import { useAppDispatch, useAppSelector } from '../../store';
import { logout } from '../../store/slices/authSlice';


const { Header, Content, Footer } = Layout;
const { Text } = Typography;
const { useBreakpoint } = Grid;

const NAV_ITEMS: MenuProps['items'] = [
  { key: '/',               label: 'Home'          },
  { key: '/jobs',           label: 'Find Jobs'     },
  { key: '/companies',      label: 'Companies'     },
  { key: '/for-employers',  label: 'For Employers' },
  { key: '/about',          label: 'About'         },
];

const PublicLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { user, isAuthenticated } = useAppSelector((s) => s.auth);
  const screens = useBreakpoint();
  const [scrolled, setScrolled] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const isHome = location.pathname === '/';
  const isTransparent = isHome && !scrolled;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const selectedKey =
    location.pathname === '/' ? '/' : `/${location.pathname.split('/')[1]}`;

  const userDropdown: MenuProps['items'] = [
    { key: 'profile', icon: <UserOutlined />, label: 'My Profile', onClick: () => navigate('/profile') },
    { type: 'divider' as const },
    {
      key: 'logout', icon: <LogoutOutlined />, label: 'Logout', danger: true,
      onClick: () => { dispatch(logout()); navigate('/'); },
    },
  ];

  const textColor = isTransparent ? 'rgba(255,255,255,0.92)' : '#222';
  // Transparent (dark hero bg) → white text; scrolled/white bg → dark text matching AppLayout
  const logoColor = isTransparent ? '#fff' : '#111111';

  return (
    <Layout style={{ minHeight: '100vh', background: '#fff' }}>
      <Header
        style={{
          position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1000,
          background: isTransparent ? 'transparent' : '#fff',
          borderBottom: isTransparent ? 'none' : '1px solid #f0f0f0',
          boxShadow: isTransparent ? 'none' : '0 2px 12px rgba(0,0,0,0.07)',
          transition: 'background 0.3s, box-shadow 0.3s, border 0.3s',
          padding: screens.md ? '0 48px' : '0 20px',
          display: 'flex', alignItems: 'center', height: 56, gap: 0,
        }}
      >
        {/* Logo */}
        <div
          onClick={() => navigate('/')}
          style={{ cursor: 'pointer', marginRight: 24, flexShrink: 0 }}
        >
          <WorkWavLogo
            size={40}
            fontSize={20}
            textColor={logoColor}
            variant={isTransparent ? 'light' : 'colored'}
          />
        </div>

        {/* Nav links */}
        {screens.md && (
          <Menu
            mode="horizontal"
            selectedKeys={[selectedKey]}
            items={NAV_ITEMS}
            onClick={({ key }) => navigate(key)}
            className="public-nav"
            style={{
              flex: 1, border: 'none', background: 'transparent',
              fontWeight: 500,
              // Always remove background highlight — underline indicator only
              '--ant-menu-horizontal-item-selected-bg': 'transparent',
              '--ant-menu-item-selected-bg': 'transparent',
              '--ant-menu-dark-item-selected-bg': 'transparent',
              ...(isTransparent
                ? {
                  '--ant-menu-item-color': 'rgba(255,255,255,0.85)',
                  '--ant-menu-item-hover-color': '#fff',
                  '--ant-menu-horizontal-item-selected-color': '#fff',
                  '--ant-menu-horizontal-item-hover-color': '#fff',
                  '--ant-menu-active-bar-border-width': '2px',
                  '--ant-menu-active-bar-color': '#fff',
                }
                : {}),
            } as React.CSSProperties}
            theme={isTransparent ? 'dark' : 'light'}
          />
        )}

        {/* Mobile hamburger (replaces nav links on small screens) */}
        {!screens.md && (
          <MenuOutlined
            style={{ fontSize: 20, cursor: 'pointer', color: textColor, marginLeft: 'auto', marginRight: 8 }}
            onClick={() => setDrawerOpen(true)}
          />
        )}

        {/* Right controls — desktop only for auth buttons; mobile via drawer */}
        <Space size={8} style={{ marginLeft: screens.md ? 'auto' : 0, flexShrink: 0 }}>
          {screens.md && (
            <PWAInstallButton
              compact={false}
              textColor={isTransparent ? 'rgba(255,255,255,0.9)' : undefined}
            />
          )}

          {/* Country selector dropdown */}
          {screens.md && <CountrySelector transparent={isTransparent} size="small" />}

          {isAuthenticated ? (
            <Dropdown menu={{ items: userDropdown }} trigger={['click']} placement="bottomRight">
              <Space style={{ cursor: 'pointer' }}>
                <Avatar
                  size={34}
                  style={{ background: '#56B0FF', fontWeight: 700, fontSize: 13 }}
                >
                  {user?.firstName?.[0]?.toUpperCase() ?? 'U'}
                </Avatar>
                {screens.md && (
                  <Text style={{ color: textColor, fontWeight: 500, transition: 'color 0.3s' }}>
                    {user?.firstName}
                  </Text>
                )}
              </Space>
            </Dropdown>
          ) : screens.md ? (
            <>
              <Button
                type="text"
                onClick={() => navigate('/login')}
                style={{ color: textColor, fontWeight: 500, transition: 'color 0.3s' }}
              >
                Log in
              </Button>
              <Button type="primary" onClick={() => navigate('/register')} style={{ fontWeight: 600 }}>
                Sign up free
              </Button>
            </>
          ) : null}
        </Space>
      </Header>

      {/* Each child page manages its own top offset to clear the fixed 56px
          header. Home: hero padding is 88px/76px (56 + breathing room).
          Other pages (Find Jobs, Companies, About, detail views): each has
          its own paddingTop: 56 on its outermost wrapper. */}
      <Content>
        <Suspense fallback={<PageLoader />}>
          <Outlet />
        </Suspense>
      </Content>

      {/* Mobile navigation drawer */}
      <Drawer
        title={<WorkWavLogo size={32} fontSize={16} textColor="#56B0FF" variant="colored" />}
        placement="left"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        width={260}
        styles={{ body: { padding: 0 } }}
      >
        <Menu
          mode="inline"
          selectedKeys={[selectedKey]}
          items={NAV_ITEMS}
          onClick={({ key }) => { navigate(key); setDrawerOpen(false); }}
          style={{ border: 'none', fontSize: 15 }}
        />
        <div style={{ borderTop: '1px solid #f0f0f0', margin: '8px 16px' }} />
        {isAuthenticated ? (
          <div style={{ padding: '8px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
            <Button block onClick={() => { navigate('/profile'); setDrawerOpen(false); }}>
              <UserOutlined /> My Profile
            </Button>
            <Button block danger onClick={() => { dispatch(logout()); navigate('/'); setDrawerOpen(false); }}>
              <LogoutOutlined /> Logout
            </Button>
          </div>
        ) : (
          <div style={{ padding: '8px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
            <Button block onClick={() => { navigate('/login'); setDrawerOpen(false); }}>Sign In</Button>
            <Button block type="primary" onClick={() => { navigate('/register'); setDrawerOpen(false); }}>Sign Up Free</Button>
          </div>
        )}
      </Drawer>

      {/* Cookie consent banner */}
      <CookieBanner />

      {/* Footer */}
      <Footer style={{ background: '#0f172a', padding: 0 }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '56px 40px 32px' }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: screens.md ? 'repeat(4, 1fr)' : 'repeat(2, 1fr)',
            gap: 40, marginBottom: 48,
          }}>
            {/* Brand */}
            <div>
              <div style={{ marginBottom: 16 }}>
                <WorkWavLogo size={32} fontSize={18} textColor="#fff" variant="light" />
              </div>
              <Text style={{ color: '#94a3b8', lineHeight: 1.8, fontSize: 14 }}>
                Ride your career wave — connecting ambitious talent with world-class opportunities.
              </Text>
            </div>

            {/* For Job Seekers */}
            <div>
              <Text style={{ color: '#f1f5f9', fontWeight: 700, display: 'block', marginBottom: 16, fontSize: 14, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Job Seekers
              </Text>
              {[['Find Jobs', '/jobs'], ['Companies', '/companies'], ['How It Works', '/how-it-works'], ['FAQ', '/faq']].map(([label, to]) => (
                <div key={label} style={{ marginBottom: 10 }}>
                  <Link to={to} style={{ color: '#94a3b8', fontSize: 14 }}
                    onMouseEnter={e => (e.currentTarget.style.color = '#e2e8f0')}
                    onMouseLeave={e => (e.currentTarget.style.color = '#94a3b8')}
                  >{label}</Link>
                </div>
              ))}
            </div>

            {/* For Employers */}
            <div>
              <Text style={{ color: '#f1f5f9', fontWeight: 700, display: 'block', marginBottom: 16, fontSize: 14, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Employers
              </Text>
              {[['For Employers', '/for-employers'], ['Post a Job', '/register'], ['Pricing', '/pricing'], ['Browse Talent', '/register']].map(([label, to]) => (
                <div key={label} style={{ marginBottom: 10 }}>
                  <Link to={to} style={{ color: '#94a3b8', fontSize: 14 }}
                    onMouseEnter={e => (e.currentTarget.style.color = '#e2e8f0')}
                    onMouseLeave={e => (e.currentTarget.style.color = '#94a3b8')}
                  >{label}</Link>
                </div>
              ))}
            </div>

            {/* Company */}
            <div>
              <Text style={{ color: '#f1f5f9', fontWeight: 700, display: 'block', marginBottom: 16, fontSize: 14, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Company
              </Text>
              {[['About Us', '/about'], ['Contact Us', '/contact'], ['Privacy Policy', '/privacy'], ['Terms of Service', '/terms']].map(([label, to]) => (
                <div key={label} style={{ marginBottom: 10 }}>
                  <Link to={to} style={{ color: '#94a3b8', fontSize: 14 }}
                    onMouseEnter={e => (e.currentTarget.style.color = '#e2e8f0')}
                    onMouseLeave={e => (e.currentTarget.style.color = '#94a3b8')}
                  >{label}</Link>
                </div>
              ))}
            </div>
          </div>

          <div style={{ borderTop: '1px solid #1e293b', paddingTop: 28, textAlign: 'center' }}>
            <Text style={{ color: '#64748b', fontSize: 13 }}>
              &copy; {new Date().getFullYear()} WorkWav. All rights reserved.
            </Text>
          </div>
        </div>
      </Footer>
    </Layout>
  );
};

export default PublicLayout;
